import React, { useEffect, useState, useRef, useImperativeHandle, forwardRef, useContext } from "react";
import KendoTreeView from "./SearchTreeView";
import { t } from "i18next";
import Sketch from '@arcgis/core/widgets/Sketch';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { addGraphicsToLayer, updateTooltipText } from '../../../../utils/Helpers';
import { removeGraphics } from "./SearchUtils";
import { SearchContext } from "./Search";
import { useTheme } from "../../../../components/ThemeSwitcher/ThemeSwitcher";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import Select from "react-select";
import CustomDropdownIndicator from "../../../../components/common/CustomDropdownIndicator";
import { updateImages } from "../../../../utils/theme";
const MapSearch = forwardRef((props, ref) => {
    const { mapview, showLoading, setEnableActions, setShowTooltip, tooltipRef, showTooltip } = useContext(SearchContext);
    const { layers } = props;
    const bufferUnits = window.searchBuffer.units;
    const [sketchTool, setSketchTool] = useState(undefined);
    const [activeTool, setActiveTool] = useState(null);
    const [selectedKeys, setSelectedKeys] = React.useState([]);
    const [keysUpdated, setKeysUpdated] = useState(false);
    const [bufferDistance, setBufferDistance] = useState(100);
    const [bufferUnit, setBufferUnit] = useState(bufferUnits[0]);
    const [queryGeometry, setQueryGeometry] = useState(undefined);
    const ktreeViewRef = useRef(null);
    const activeToolRef = useRef(activeTool);
    const sketchToolRef = useRef(sketchTool);
    let { theme } = useTheme();
    theme = localStorage.getItem("theme") || theme
    let sketchGLayer = useRef(new GraphicsLayer({ id: 'searchDrawGLayer' }));
    let clipledGraphics = useRef(new GraphicsLayer({
        id: "clipedGraphicsLayer",
        title: "Clipped Graphics Layer",
    }));
    const bufferDistanceRef = useRef(bufferDistance);
    const bufferUnitRef = useRef(bufferUnit);
    const [validLayers, setValidLayers] = useState([]);

    useEffect(() => {
        bufferDistanceRef.current = bufferDistance;
        bufferUnitRef.current = bufferUnit;
    }, [bufferDistance, bufferUnit]);
    useEffect(() => {
        if (!mapview) return;

        // if (layers.length > 0) {
        //     console.log("layers", JSON.stringify(layers));
        //     showLoading(false);
        // }
        if (layers.length > 0) {
            // Check if layers are really present in the map
            const mapLayerIds = mapview.map.layers.map(l => l.id);
            const filteredLayers = layers.filter(layer => mapLayerIds.includes(layer.id));

            setValidLayers(filteredLayers);

            // console.log("Valid Layers for TreeView:", JSON.stringify(filteredLayers));
            showLoading(false);
        } else {
            setValidLayers([]); // No layers to show
        }
        initializeSketch();
        mapview.map.add(sketchGLayer.current);
        mapview.map.add(clipledGraphics.current);
    }, [mapview, layers])


    const updateTheme = (event) => {
        const newTheme = event.detail;
        theme = newTheme;
        updateImages(theme);
        // applyTheme(elementInstances, theme,"AirQuality")
    };

    useEffect(() => {
        window.addEventListener("themeChanged", updateTheme);

        return () => {
            window.removeEventListener("themeChanged", updateTheme);
        };
    }, [])

    const initializeSketch = () => {
        let sketchTool = new Sketch({
            view: mapview,
            layer: sketchGLayer.current,
            availableCreateTools: ['polygon', 'point', 'polyline'],

        });
        sketchTool.viewModel.polygonSymbol = {
            type: "simple-fill",
            // color: [0, 0, 255, 0.3], // Blue fill with 30% opacity
            outline: {
                color: "#1b8096", // Blue border
                width: 2
            }
        };

        sketchTool.viewModel.polylineSymbol = {
            type: "simple-line",
            color: [27, 128, 150], // Line color
            width: 2
        };

        sketchTool.viewModel.pointSymbol = {
            type: "simple-marker",
            color: [27, 128, 150], // Point color
            outline: {
                color: [255, 255, 255], // White outline
                width: 1
            }
        };

        setSketchTool(sketchTool);
    }

    useEffect(() => {
        activeToolRef.current = activeTool;
        sketchToolRef.current = sketchTool;
        if (!sketchTool || !activeTool) {
            setShowTooltip(false);
            cancelSketch();
            return;
        };
        setShowTooltip(true);

        if (!showTooltip || !tooltipRef || !tooltipRef.current) {
            return;
        }
        switch (activeTool) {
            case 'pointBuffer':
                tooltipRef.current.innerHTML = t('clickToAddPoint');
                tooltipRef.current.classList.add('mapTooltip');
                sketchTool.create('point');
                break;
            case 'distanceBuffer':
                tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
                tooltipRef.current.classList.add('mapTooltip');
                sketchTool.create('polyline');
                break;
            case 'MapPolygonBuffer':
                tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
                tooltipRef.current.classList.add('mapTooltip');
                sketchTool.create('polygon');
                break;
            default:
                tooltipRef.current.innerHTML = t('clickOnMapToStartDrawing');
                tooltipRef.current.classList.add('mapTooltip');
                sketchTool.create(activeTool);
                break;
        }
        // const handleCreate = (event) => {
        //     if (event.aborted) {
        //         return;
        //     }

        //     // Ensure tooltip text is based on the correct tool
        //     if (activeToolRef.current === 'pointBuffer') {
        //         tooltipRef.current.innerHTML = t('clickToAddPoint');
        //         tooltipRef.current.classList.add('mapTooltip');
        //     } else {
        //         updateTooltipText(event, sketchTool, tooltipRef, t);
        //     }

        //     if (event.state === "complete") {
        //         const graphic = event.graphic;
        //         addGraphicsToLayer(sketchGLayer.current, [graphic]);
        //         setQueryGeometry(graphic.geometry);
        //         setShowTooltip(true)
        //         sketchTool.cancel();
        //         setActiveTool(null)
        //     }
        // };

        const handleCreate = (event) => {
            if (event.aborted) return;

            if (event.state === "complete") {
                const graphic = event.graphic;
                let bufferedGeometry = null;

                if (activeTool === 'pointBuffer' || activeTool === 'distanceBuffer' || activeTool === 'MapPolygonBuffer') {
                    if (graphic.geometry) {
                        let unit = bufferUnitRef.current?.value || bufferUnitRef.current;
                        bufferedGeometry = geometryEngine.buffer(graphic.geometry, bufferDistanceRef.current, unit);
                    }
                    // if (graphic.geometry) {
                    //     let unit = bufferUnit?.value || bufferUnit;
                    //     bufferedGeometry = geometryEngine.buffer(graphic.geometry, bufferDistance, unit);
                    // }
                }

                if (bufferedGeometry) {
                    addGraphicsToLayer(sketchGLayer.current, [{ geometry: bufferedGeometry, symbol: graphic.symbol }]);
                    setQueryGeometry(bufferedGeometry);
                } else {
                    addGraphicsToLayer(sketchGLayer.current, [graphic]);
                    setQueryGeometry(graphic.geometry);
                }

                setShowTooltip(true);
                sketchTool.cancel();
                setActiveTool(null);
            }
        };
        const createHandle = sketchTool.on('create', handleCreate);

        return () => {
            if (createHandle) {
                createHandle.remove();
            }
        };

    }, [showTooltip, sketchTool, activeTool, tooltipRef, t])

    const updateSelectedKeys = (newSelectedKeys) => {
        setKeysUpdated(true);
        setSelectedKeys(newSelectedKeys);
    };

    const handleToolClick = (tool) => {
        if (activeTool && activeTool === tool) {
            setActiveTool(null);
        } else {
            removeGraphics(mapview, sketchGLayer.current);
            removeGraphics(mapview, clipledGraphics.current);
            setActiveTool(tool);
        }


    }

    const handleBufferDistanceChange = (evt) => {
        const newBufferDistance = evt.target.valueAsNumber || Number(evt.target.value);
        setBufferDistance(newBufferDistance);
    }

    const handleBufferUnitChange = (evt) => {
        setBufferUnit(evt);
    }

    const getQueryOptions = () => {
        let queryOptions = {};
        queryOptions.geometry = queryGeometry;
        queryOptions.distance = bufferDistance;
        queryOptions.units = bufferUnit;
        queryOptions.spatialRelationship = 'intersects';

        return queryOptions;
    }

    const cancelSketch = () => {
        if (sketchTool) {
            sketchTool.cancel();
        }
        if (sketchToolRef.current) {
            sketchToolRef.current.cancel();
        }

    }

    const reset = () => {
        setQueryGeometry(undefined);
        setKeysUpdated(false);
        setActiveTool(null);
        setBufferDistance(100);
        setBufferUnit(bufferUnits[0]);
        cancelSketch();
        // if (ktreeViewRef && ktreeViewRef.current) {
        //     ktreeViewRef.current.selectAll();
        // }
        const mapLayerIds = mapview?.map?.layers.map(l => l.id) || [];
        const filteredLayers = layers.filter(layer => mapLayerIds.includes(layer.id));
        const clippedGraphics = mapview?.map?.findLayerById("clipedGraphicsLayer");
        if (clippedGraphics) {
            removeGraphics(mapview, clippedGraphics);
        }

        // Update TreeView with only valid layers
        setValidLayers(filteredLayers);

        // if (filteredLayers.length > 0) {
        //     ktreeViewRef.current.selectAll();
        // }
        removeGraphics(mapview, sketchGLayer.current);
        removeGraphics(mapview, clipledGraphics.current);
        setShowTooltip(false);
    }

    useImperativeHandle(ref, () => ({
        getSelectedItems: () => {
            if (ktreeViewRef.current) {
                const selectedItems = ktreeViewRef.current.getSelectedItems();
                return selectedItems;
            }
            return [];
        },
        getQueryOptions,
        cancelSketch,
        reset,
        setActiveTool
    }));

    useEffect(() => {
        if (!selectedKeys || selectedKeys.length === 0 || !queryGeometry) {
            setEnableActions(false);/**submit should not be enabled if no layer is selected */
        } else {
            setEnableActions(true);
        }

    }, [activeTool, selectedKeys, queryGeometry, setEnableActions])

    useEffect(() => {
        return () => {

            reset();


        }
    }, [])
    const options = bufferUnits?.map(bunit => ({
        value: bunit,
        label: t(`${bunit}`)
    }));
    const handleSelectChange = (selectedOption) => {
        handleBufferUnitChange(selectedOption.value); // Pass the selected value to the handler
    };
    return (
        <div className="map-search search-scroll-content">
            <ul className="draw-tools mb-3 nav nav-tabs mt-1">
                <li className={` nav-item ${activeTool === 'polygon' ? 'active' : ''}`} onClick={() => handleToolClick('polygon')}>

                    <button
                        className={`nav-link ${activeTool === 'polygon' ? 'active' : ''}`}

                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        type="button"
                        role="tab"
                        aria-controls="home"

                        aria-selected="true"
                    >
                        {/* <FaDrawPolygon size={22} /> */}
                        <img className="wid-35Img"
                            src={theme === "dark"
                                ? `${process.env.PUBLIC_URL}/images/DarkPolygon-buffer.svg`
                                : `${process.env.PUBLIC_URL}/images/polygon.svg`}
                            alt=""
                            data-theme-img={"polygon"}
                        />

                        <span className="icon">{t('polygon')}</span>
                    </button>
                </li>
                <li className={`nav-item ${activeTool === 'MapPolygonBuffer' ? 'active' : ''}`} onClick={() => handleToolClick('MapPolygonBuffer')}>
                    <button
                        className={`nav-link ${activeTool === 'MapPolygonBuffer' ? 'active' : ''}`}

                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        type="button"
                        role="tab"
                        aria-controls="home"

                        aria-selected="true"
                    >
                        {/* <FaRegDotCircle size={22} /> */}
                        <img className="wid-35Img"
                            src={theme === "dark"
                                ? `${process.env.PUBLIC_URL}/images/aoiPolyNewDark.svg`
                                : `${process.env.PUBLIC_URL}/images/aoiPolyNew.svg`}
                            alt=""
                            data-theme-img={"mapPolygonBuffer"}
                        />

                        <span className="icon">{t('PolygonBuffer')}</span>
                    </button>
                </li>
                <li className={`nav-item ${activeTool === 'pointBuffer' ? 'active' : ''}`} onClick={() => handleToolClick('pointBuffer')}>
                    <button
                        className={`nav-link ${activeTool === 'pointBuffer' ? 'active' : ''}`}

                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        type="button"
                        role="tab"
                        aria-controls="home"

                        aria-selected="true"
                    >
                        {/* <FaRegDotCircle size={22} /> */}
                        <img className="wid-35Img"
                            src={theme === "dark"
                                ? `${process.env.PUBLIC_URL}/images/Drakpoint-buffer.svg`
                                : `${process.env.PUBLIC_URL}/images/point-buffer.svg`}
                            alt=""
                            data-theme-img={"point"}
                        />

                        <span className="icon">{t('pointBuffer')}</span>
                    </button>
                </li>
                <li className={`nav-item ${activeTool === 'distanceBuffer' ? 'active' : ''}`} onClick={() => handleToolClick('distanceBuffer')}>
                    <button
                        className={`nav-link ${activeTool === 'distanceBuffer' ? 'active' : ''}`}

                        data-bs-toggle="tab"
                        data-bs-target="#home"
                        type="button"
                        role="tab"
                        aria-controls="home"

                        aria-selected="true"
                    >
                        {/* <PiLinkSimpleHorizontal size={22} /> */}
                        <img className="wid-35Img"
                            src={theme === "dark"
                                ? `${process.env.PUBLIC_URL}/images/darkDistane-buffer.svg`
                                : `${process.env.PUBLIC_URL}/images/distance-buffer.svg`}
                            alt=""
                            data-theme-img={"distance"}
                        />

                        <span className="icon">{t('distanceBuffer')}</span>
                    </button>
                </li>
            </ul>
            {activeTool && activeTool !== 'polygon' &&
                <div className="row buffer-options p-0 d-flex-inline">

                    <div className="col-lg-6 col-6">
                        <div className="form-group">
                            <label className="label">{t('distance')}</label>
                            <input type="number" className="input bufferDistance form-control" value={bufferDistance} onChange={handleBufferDistanceChange} />
                        </div>
                    </div>
                    <div className="col-lg-6 col-6">
                        <div className="form-group">
                            <label className="label">{t('Units')}</label>

                            <Select
                                components={{
                                    DropdownIndicator: CustomDropdownIndicator,
                                }}
                                className={`react-select-container`}
                                classNamePrefix="react-select"
                                options={options}
                                // value={bufferUnit}
                                value={options ? options.find(option => option.value === bufferUnit) : null}
                                onChange={handleBufferUnitChange}

                            />
                        </div>
                    </div>

                </div>
            }

            <h6 style={{ fontSize: '0.8rem' }}>{t('Selectable Layer')}</h6>
            <div className={`bufferScrolll-point ${activeTool === 'pointBuffer' || activeTool === 'distanceBuffer' || activeTool === 'MapPolygonBuffer' ? 'bufferScrolll-polygon' : ''}`} >
                <KendoTreeView
                    data={validLayers}
                    selectedKeys={selectedKeys}
                    updateSelectedKeys={updateSelectedKeys}
                    onExpandChange={updateSelectedKeys}
                    keysUpdated={keysUpdated}
                    ref={ktreeViewRef}
                />
            </div>

        </div>
    )
});

export default MapSearch;